import { SurgeryRequest } from "../types/pdf.types";

/** "12345678901" → "123.456.789-01", aplicado enquanto a pessoa digita. */
export function formatCpf(value: string): string {
  return value
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

/** "2026-11-03" → "03/11/2026". */
export function isoToBrDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
}

/** "2026-11-03T14:30" → "03/11/2026, às 14:30". Sem data (pedido antigo), vazio. */
export function formatDesiredDateTime(iso?: string): string {
  const [date, time] = (iso ?? "").split("T");
  if (!date) return "";
  return time ? `${isoToBrDate(date)}, às ${time}` : isoToBrDate(date);
}

/** Campos do pedido a partir do texto gerado pela IA; procedimentos, OPME, hospital e data vêm do rascunho. */
export function parseSurgeryRequest(
  text: string,
  draft: { desiredDate?: string; opmeSummary?: string; procedures: string; hospital?: string }
): SurgeryRequest {
  const pick = (regex: RegExp) => text.match(regex)?.[1]?.trim() || "";
  return {
    patientName: pick(/Nome do paciente:\s*(.*)/),
    patientDescription: pick(/Descrição do paciente:\s*([\s\S]*?)(?=\n?-{3,})/),
    opmeSummary: draft.opmeSummary ?? "",
    procedures: draft.procedures,
    cids: pick(/CID's:\s*([\s\S]*?)(?=\n?-{3,})/),
    issueDate: pick(/Data de emissão:\n(.*)/),
    desiredDateTime: formatDesiredDateTime(draft.desiredDate),
    hospital: draft.hospital ?? "",
  };
}

/** Procedimentos do pedido, um por linha: "código quantidade descrição". */
export function formatProcedureLines(procedures: Array<{ code: string; description: string; quantity: string }>): string {
  return procedures.map((p) => `${p.code} ${p.quantity} ${p.description}`).join("\n");
}

export function transformDateAndHour(text: string): string {
  const regex = /(\d{2})\/(\d{2})\/(\d{4})\D+(\d{2}):(\d{2})/g;
  let match;
  let formattedDateTime = "";
  while ((match = regex.exec(text)) !== null) {
    const [, dia, mes, ano, hora, minuto] = match;
    formattedDateTime = `${ano}-${mes}-${dia}T${hora}:${minuto}`;
  }
  return formattedDateTime;
}

// Remove acentos para comparação — o usuário pode digitar "Clinica" enquanto o
// nome cadastrado é "Clínica", e isso não deve fazer a sugestão sumir da lista.
export const normalizeForCompare = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const capitalizeFullName = (name: string): string => {
  return name.replace(/\b[a-zA-Zá-úÁ-ÚõÕãÃçÇ]+/g, (match, index) => {
    // Apply size rule: words with 2 letters or less (ex: e, da, de, do) 
    // remain lowercase, unless it's the first word of the name
    if (match.length <= 2 && index > 0) {
      return match.toLowerCase();
    }

    // Larger words (or the 1st word) have the initial capitalized and the rest lowercase
    return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
  });
};

export function formatSurgeryRequest(data: SurgeryRequest): string {
  const surgery_request = `
    Aqui está seu pedido de cirurgia:
    -------------------------------------------------
    Nome do paciente:
    ${data.patientName}
    -------------------------------------------------
    Descrição do paciente:
    ${data.patientDescription}
    -------------------------------------------------
    Procedimentos:
    ${data.procedures}
    -------------------------------------------------
    CID's:
    ${data.cids}
    -------------------------------------------------
    Data de emissão:
    ${data.desiredDateTime}
    -------------------------------------------------
    Hospital:
    ${data.hospital}
    -------------------------------------------------`;
  return surgery_request;
}

// Máscara progressiva: dd/mm/aaaa. O <input type="date"> nativo mostra o
// formato do idioma do navegador (dd/mm/yyyy em inglês) — texto com máscara
// garante o placeholder em português.
export function formatDateMaskBR(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

// "dd/mm/aaaa" → Date local à meia-noite, ou null se incompleta/inexistente
// (ex.: 31/02/2026).
export function parseDateBR(value: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!m) return null;
  const [dia, mes, ano] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(ano, mes - 1, dia);
  return d.getFullYear() === ano && d.getMonth() === mes - 1 && d.getDate() === dia ? d : null;
}

// Máscara progressiva: +55 (DD) 99999-9999
export function formatPhoneBR(value: string, previousValue = ""): string {
  let digits = value.replace(/\D/g, "").replace(/^55/, "").slice(0, 11);

  // O ")" depois do DDD (e o espaço antes do restante do número) são
  // inseridos pela própria máscara, não digitados pelo médico. Se ele apaga
  // um desses caracteres, a quantidade de dígitos não muda — e no próximo
  // render a máscara recoloca o caractere removido, dando a impressão de que
  // o backspace "travou" e não é possível apagar o número inteiro. Detectando
  // esse caso (apagou um caractere, mas os dígitos continuam os mesmos),
  // removemos também o último dígito para o backspace ter efeito de verdade.
  const previousDigits = previousValue.replace(/\D/g, "").replace(/^55/, "").slice(0, 11);
  if (value.length < previousValue.length && digits.length === previousDigits.length && digits.length > 0) {
    digits = digits.slice(0, -1);
  }

  if (!digits) return "";

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);

  let formatted = `+55 (${ddd}`;
  if (ddd.length === 2) formatted += ")";
  if (rest.length > 5) {
    formatted += ` ${rest.slice(0, 5)}-${rest.slice(5)}`;
  } else if (rest.length > 0) {
    formatted += ` ${rest}`;
  }
  return formatted;
}

// Máscara progressiva: XX.XXX.XXX/XXXX-XX
export function formatCnpj(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14);

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}